<script setup>
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { useLoading } from "vue-loading-overlay";
import { errorNotify, successNotify } from "@/mixins";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import FormModal from "@/components/modals/FormModal.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const $loading = useLoading({});

// ---- data ----
const organizationList = computed(() => store.getters['organization/organizationList']);
const userList = computed(() => store.getters['user/userList']);
const tariffList = computed(() => store.getters['billing/tariffList']);
const cityList = computed(() => store.getters['city/cityList']);
const addressList = computed(() => store.getters['address/addressList']);

const orgs = computed(() => organizationList.value?.results || []);
const employees = computed(() => userList.value?.results || []);
const tariffs = computed(() => tariffList.value?.results || tariffList.value || []);
const cities = computed(() => cityList.value?.results || []);
const addresses = computed(() => addressList.value?.results || []);

const orgPage = ref(1);
const activeId = ref(null);
const active = computed(() => orgs.value.find((o) => o.id === activeId.value) || null);

const empPage = ref(1);

// ---- loading ----
function loadOrgs(keepActive = true) {
  const loader = $loading.show({});
  return store.dispatch('organization/getOrganizationList', `?page=${orgPage.value}`)
    .then(() => {
      if (!keepActive || !orgs.value.some((o) => o.id === activeId.value)) {
        activeId.value = orgs.value[0]?.id ?? null;
      }
    })
    .finally(() => loader.hide());
}

function loadEmployees() {
  if (!activeId.value) return Promise.resolve();
  return store.dispatch('user/getUserList', `?organization=${activeId.value}&page=${empPage.value}`);
}

watch(activeId, () => {
  empPage.value = 1;
  loadEmployees();
});

onMounted(() => {
  loadOrgs(false);
  store.dispatch('billing/getTariffList', '');
  store.dispatch('city/getCityList', '');
  store.dispatch('address/getAddressList', '');
});

// ---- helpers ----
const initials = (name) =>
  String(name || '')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const orgAddress = (org) =>
  org?.address ? [org.address.line1, org.address.city?.name].filter(Boolean).join(', ') : '';

const TARIFF_META = {
  pay_per_use: { cls: 'payperuse', icon: 'timer-outline', label: 'Pay-per-use' },
  monthly: { cls: 'flat', icon: 'calendar-sync-outline', label: 'Flat Monthly' },
};

const activeTariff = computed(() =>
  tariffs.value.find ? tariffs.value.find((t) => t.id === active.value?.tariff) : null
);

const tariffMeta = computed(() => TARIFF_META[activeTariff.value?.type] || null);

const tariffRate = computed(() => {
  const t = activeTariff.value;
  if (!t) return '—';
  if (t.type === 'monthly' && t.monthly_price != null) return `${t.monthly_price} / month`;
  if (t.type === 'pay_per_use' && t.price_per_use != null) return `${t.price_per_use} / order`;
  return t.name;
});

const ROLE_META = {
  administrator: { cls: 'admin', icon: 'shield-account', label: 'Admin' },
  manager: { cls: 'manager', icon: 'account-tie', label: 'Manager' },
  employee: { cls: 'staff', icon: 'account', label: 'Staff' },
};
const roleMeta = (role) => ROLE_META[role] || { cls: 'staff', icon: 'account', label: role || '—' };

// ---- org CRUD ----
const orgModal = ref(null); // { mode: 'add'|'edit', data }
const orgSubmitting = ref(false);

const orgFields = computed(() => [
  { key: 'name', label: 'Organization name', placeholder: 'Acme Inc.', required: true, full: true },
  { key: 'email', label: 'Email', placeholder: 'admin@acme.com', type: 'email', required: true, full: true },
  {
    key: 'city', label: 'City', type: 'select', required: true, placeholder: 'Select city',
    options: cities.value.map((c) => ({ value: String(c.id), label: c.name })),
  },
  {
    key: 'address', label: 'Address', type: 'select', required: true, dependsOn: 'city',
    placeholder: 'Select address', placeholderDisabled: 'Select city first…',
    options: (vals) => addresses.value
      .filter((a) => String(a.city?.id) === String(vals.city))
      .map((a) => ({ value: String(a.id), label: a.line1 })),
  },
]);

function openOrgModal(mode, org) {
  orgModal.value = {
    mode,
    data: mode === 'edit'
      ? {
          id: org.id,
          name: org.name,
          email: org.email,
          city: org.address?.city?.id != null ? String(org.address.city.id) : '',
          address: org.address?.id != null ? String(org.address.id) : '',
        }
      : {},
  };
}

function saveOrg(vals) {
  orgSubmitting.value = true;
  const data = { name: vals.name, email: vals.email, address: vals.address };
  const action = orgModal.value.mode === 'add'
    ? store.dispatch('organization/createOrganization', data)
    : store.dispatch('organization/updateOrganization', { id: orgModal.value.data.id, data });
  action
    .then(() => {
      const err = orgModal.value.mode === 'add'
        ? store.getters['organization/createOrganizationError']
        : store.getters['organization/updateOrganizationError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify(orgModal.value.mode === 'add' ? 'Organization created' : 'Organization updated');
        orgModal.value = null;
        loadOrgs();
      }
    })
    .finally(() => { orgSubmitting.value = false; });
}

// ---- tariff ----
const tariffModal = ref(false);
const tariffFields = computed(() => [
  {
    key: 'tariff', label: 'Tariff', type: 'select', required: true, full: true, placeholder: 'Select tariff',
    options: tariffs.value.map((t) => ({
      value: String(t.id),
      label: `${t.name} — ${TARIFF_META[t.type]?.label || t.type}`,
    })),
  },
]);

function saveTariff(vals) {
  store.dispatch('organization/updateOrganization', { id: active.value.id, data: { tariff: vals.tariff } })
    .then(() => {
      const err = store.getters['organization/updateOrganizationError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify('Tariff updated');
        tariffModal.value = false;
        loadOrgs();
      }
    });
}

// ---- employee CRUD ----
const empModal = ref(null); // { mode, data }
const empSubmitting = ref(false);

const empFields = computed(() => {
  const fields = [
    { key: 'first_name', label: 'Full name', placeholder: 'Jane Doe', required: true, full: true },
    {
      key: 'organization', label: 'Organization', type: 'select', required: true, full: true,
      placeholder: 'Select organization',
      options: orgs.value.map((o) => ({ value: String(o.id), label: o.name })),
    },
    {
      key: 'role', label: 'Role', type: 'select', required: true, placeholder: 'Select role',
      options: Object.entries(ROLE_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
  ];
  if (empModal.value?.mode === 'edit') {
    fields.push({ key: 'email', label: 'Email', readonly: true });
  } else {
    fields.push({ key: 'email', label: 'Email', placeholder: 'jane@acme.com', type: 'email', required: true });
  }
  return fields;
});

function openEmpModal(mode, emp) {
  empModal.value = {
    mode,
    data: mode === 'edit'
      ? {
          id: emp.id,
          first_name: emp.first_name,
          email: emp.email,
          role: emp.role,
          organization: String(activeId.value),
        }
      : { organization: String(activeId.value) },
  };
}

function saveEmp(vals) {
  empSubmitting.value = true;
  const targetOrg = Number(vals.organization);
  const data = { first_name: vals.first_name, role: vals.role, organizations: [targetOrg] };
  let action;
  if (empModal.value.mode === 'add') {
    action = store.dispatch('user/createUser', { ...data, email: vals.email });
  } else {
    action = store.dispatch('user/updateUserData', { id: empModal.value.data.id, data });
  }
  action
    .then(() => {
      const err = empModal.value.mode === 'add'
        ? store.getters['user/createUserError']
        : store.getters['user/updateUserError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify(empModal.value.mode === 'add' ? 'Employee added' : 'Employee updated');
        empModal.value = null;
        loadEmployees();
        loadOrgs();
      }
    })
    .finally(() => { empSubmitting.value = false; });
}

// ---- delete ----
const pendingDelete = ref(null); // { kind: 'org'|'emp', data }

function confirmDelete() {
  const { kind, data } = pendingDelete.value;
  if (kind === 'org') {
    store.dispatch('organization/deleteOrganization', data.id).then(() => {
      const err = store.getters['organization/deleteOrganizationError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify('Organization deleted');
        activeId.value = null;
        loadOrgs(false);
      }
      pendingDelete.value = null;
    });
  } else {
    store.dispatch('user/deleteUser', data.id).then(() => {
      const err = store.getters['user/deleteUserError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify('Employee removed');
        loadEmployees();
        loadOrgs();
      }
      pendingDelete.value = null;
    });
  }
}

const empCols = 'minmax(170px,1.3fr) 150px minmax(200px,1.5fr)';
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-primary" type="button" @click="openOrgModal('add')">
      <span class="mdi mdi-plus"></span>
      Add Organization
    </button>
  </Teleport>

  <div class="org-d3">
    <div class="orgwrap">
      <!-- left: master list -->
      <div class="orglist">
        <div class="orglist__head">
          <span class="mdi mdi-office-building-outline" style="color: var(--lb-blue); font-size: 18px;"></span>
          <h2>Organizations</h2>
          <span class="count">{{ organizationList?.count ?? orgs.length }}</span>
        </div>
        <button
          v-for="org in orgs"
          :key="org.id"
          class="orgcard"
          :class="{ 'is-active': org.id === activeId }"
          type="button"
          @click="activeId = org.id">
          <div class="orgcard__logo">{{ initials(org.name) }}</div>
          <div class="orgcard__body">
            <div class="orgcard__name">{{ org.name }}</div>
            <div class="orgcard__meta">
              <span class="mdi mdi-email-outline"></span>
              {{ org.email }}
            </div>
            <div class="orgcard__count">
              <span class="mdi mdi-account-group-outline"></span>
              {{ (org.users || []).length }} employees
            </div>
          </div>
          <span class="orgcard__edit" title="Edit" @click.stop="openOrgModal('edit', org)">
            <span class="mdi mdi-pencil-outline"></span>
          </span>
        </button>
        <PaginationBar
          v-if="(organizationList?.total_pages || 1) > 1"
          :page="orgPage"
          :total-pages="organizationList?.total_pages || 1"
          @update:page="(p) => { orgPage = p; loadOrgs(); }" />
      </div>

      <!-- right: detail -->
      <div v-if="active" class="orgdetail">
        <div class="orgdetail__head">
          <div class="orgdetail__logo">{{ initials(active.name) }}</div>
          <div class="orgdetail__info">
            <h2>{{ active.name }}</h2>
            <div class="meta">
              <span><span class="mdi mdi-email-outline"></span> {{ active.email }}</span>
              <span v-if="orgAddress(active)">
                <span class="mdi mdi-map-marker-outline"></span> {{ orgAddress(active) }}
              </span>
            </div>
          </div>
          <button class="btn-ghost" type="button" @click="openOrgModal('edit', active)">
            <span class="mdi mdi-pencil-outline"></span> Edit
          </button>
          <button
            class="iconbtn"
            title="Delete organization"
            type="button"
            @click="pendingDelete = { kind: 'org', data: active }">
            <span class="mdi mdi-trash-can-outline"></span>
          </button>
        </div>

        <div class="emp-head">
          <span class="mdi mdi-credit-card-outline" style="color: var(--lb-blue); font-size: 19px;"></span>
          <h3>Billing</h3>
          <div class="spacer"></div>
          <button class="btn-ghost" type="button" @click="tariffModal = true">
            <span class="mdi mdi-pencil-outline"></span> Edit tariff
          </button>
        </div>
        <div class="card billing-row">
          <span v-if="tariffMeta" class="planchip" :class="`planchip--${tariffMeta.cls}`">
            <span class="mdi" :class="`mdi-${tariffMeta.icon}`"></span>
            {{ tariffMeta.label }}
          </span>
          <span v-else class="planchip planchip--payperuse">
            <span class="mdi mdi-help-circle-outline"></span> No tariff
          </span>
          <div class="billing-stat">
            <div class="billing-stat__label">Rate</div>
            <div class="billing-stat__value">{{ tariffRate }}</div>
          </div>
          <div class="billing-stat">
            <div class="billing-stat__label">Balance</div>
            <div class="billing-stat__value">{{ active.balance ?? '—' }}</div>
          </div>
          <div class="billing-stat">
            <div class="billing-stat__label">Tariff since</div>
            <div class="billing-stat__value">
              {{ active.tariff_since ? new Date(active.tariff_since).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—' }}
            </div>
          </div>
        </div>

        <div class="emp-head">
          <span class="mdi mdi-account-group-outline" style="color: var(--lb-blue); font-size: 19px;"></span>
          <h3>Employees</h3>
          <span class="count">{{ userList?.count ?? employees.length }}</span>
          <div class="spacer"></div>
          <button class="btn-primary" type="button" @click="openEmpModal('add')">
            <span class="mdi mdi-account-plus-outline"></span> Add Employee
          </button>
        </div>
        <DataTable
          :cols="empCols"
          :columns="['Name', 'Role', 'Email']"
          :rows="employees"
          empty-text="No employees yet. Add the first one.">
          <template #row="{ row }">
            <div class="td">
              <div class="td-name">
                <div class="ic" style="background: rgba(141,150,178,0.16); color: var(--lb-slate);">
                  <span class="mdi mdi-account"></span>
                </div>
                <span class="strong">{{ row.first_name || '—' }}</span>
              </div>
            </div>
            <div class="td">
              <span class="rolechip" :class="`rolechip--${roleMeta(row.role).cls}`">
                <span class="mdi" :class="`mdi-${roleMeta(row.role).icon}`"></span>
                {{ roleMeta(row.role).label }}
              </span>
            </div>
            <div class="td"><span class="muted">{{ row.email }}</span></div>
            <RowActions
              :copies="[{ icon: 'email-outline', label: 'Copy email', text: row.email }]"
              detail-label="Edit"
              deletable
              @detail="openEmpModal('edit', row)"
              @delete="pendingDelete = { kind: 'emp', data: row }" />
          </template>
        </DataTable>
        <PaginationBar
          v-if="(userList?.total_pages || 1) > 1"
          :page="empPage"
          :total-pages="userList?.total_pages || 1"
          :count="userList?.count || 0"
          @update:page="(p) => { empPage = p; loadEmployees(); }" />
      </div>

      <div v-else class="orgdetail">
        <div class="card empty-detail">
          <div>
            <span class="mdi mdi-office-building-outline"></span>
            <p>No organization selected</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- modals -->
  <FormModal
    :open="!!orgModal"
    :title="orgModal?.mode === 'edit' ? 'Edit Organization' : 'Add Organization'"
    :fields="orgFields"
    :initial="orgModal?.data || {}"
    :submit-label="orgModal?.mode === 'edit' ? 'Save changes' : 'Add organization'"
    :submitting="orgSubmitting"
    @submit="saveOrg"
    @cancel="orgModal = null" />

  <FormModal
    :open="!!empModal"
    :title="empModal?.mode === 'edit' ? 'Edit Employee' : 'Add Employee'"
    :fields="empFields"
    :initial="empModal?.data || {}"
    :submit-label="empModal?.mode === 'edit' ? 'Save changes' : 'Add employee'"
    :submitting="empSubmitting"
    @submit="saveEmp"
    @cancel="empModal = null" />

  <FormModal
    :open="tariffModal"
    title="Edit Tariff"
    :fields="tariffFields"
    :initial="{ tariff: active?.tariff != null ? String(active.tariff) : '' }"
    submit-label="Save changes"
    @submit="saveTariff"
    @cancel="tariffModal = false" />

  <ConfirmModal
    :open="!!pendingDelete"
    danger
    :title="pendingDelete?.kind === 'org' ? 'Delete organization?' : 'Remove employee?'"
    :message="pendingDelete
      ? (pendingDelete.kind === 'org'
          ? `“${pendingDelete.data.name}” and all its employees will be permanently removed.`
          : `“${pendingDelete.data.first_name || pendingDelete.data.email}” will be removed.`)
      : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="pendingDelete = null" />
</template>
